<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import TelegramInternalApi from 'dashboard/api/telegramInternal';

const { t } = useI18n();

const isLoading = ref(false);
const rows = ref([]);
const summary = ref({});
const filters = ref({
  eventName: '',
  userId: '',
  since: '',
  until: '',
});

const eventSummaryRows = computed(() => {
  return Object.entries(summary.value || {})
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count);
});

const loadAudits = async () => {
  isLoading.value = true;
  try {
    const response = await TelegramInternalApi.getAudits({
      eventName: filters.value.eventName || undefined,
      userId: filters.value.userId || undefined,
      since: filters.value.since || undefined,
      until: filters.value.until || undefined,
      limit: 300,
    });
    rows.value = response.data?.items || [];
    summary.value = response.data?.summary || {};
  } finally {
    isLoading.value = false;
  }
};

onMounted(() => {
  loadAudits();
});
</script>

<template>
  <div class="flex flex-col gap-4 py-6">
    <div class="rounded-lg border border-n-weak bg-n-solid-2 p-4">
      <h1 class="text-base font-semibold text-n-slate-12">
        {{ t('TELEGRAM_INTERNAL.AUDIT.TITLE') }}
      </h1>
      <p class="text-sm text-n-slate-11">
        {{ t('TELEGRAM_INTERNAL.AUDIT.DESCRIPTION') }}
      </p>
    </div>

    <div class="rounded-lg border border-n-weak bg-n-solid-1 p-4">
      <div class="grid grid-cols-1 gap-3 md:grid-cols-5">
        <label class="flex flex-col gap-1 text-sm text-n-slate-12">
          {{ t('TELEGRAM_INTERNAL.AUDIT.EVENT_NAME') }}
          <input
            v-model="filters.eventName"
            class="rounded-md border border-n-weak bg-n-background px-2 py-2"
          />
        </label>
        <label class="flex flex-col gap-1 text-sm text-n-slate-12">
          {{ t('TELEGRAM_INTERNAL.AUDIT.USER_ID') }}
          <input
            v-model="filters.userId"
            class="rounded-md border border-n-weak bg-n-background px-2 py-2"
          />
        </label>
        <label class="flex flex-col gap-1 text-sm text-n-slate-12">
          {{ t('TELEGRAM_INTERNAL.AUDIT.SINCE') }}
          <input
            v-model="filters.since"
            type="datetime-local"
            class="rounded-md border border-n-weak bg-n-background px-2 py-2"
          />
        </label>
        <label class="flex flex-col gap-1 text-sm text-n-slate-12">
          {{ t('TELEGRAM_INTERNAL.AUDIT.UNTIL') }}
          <input
            v-model="filters.until"
            type="datetime-local"
            class="rounded-md border border-n-weak bg-n-background px-2 py-2"
          />
        </label>
        <div class="flex items-end">
          <button
            type="button"
            class="rounded-md border border-n-weak px-3 py-2 text-sm text-n-slate-12"
            @click="loadAudits"
          >
            {{ t('TELEGRAM_INTERNAL.REPORTS.REFRESH') }}
          </button>
        </div>
      </div>
    </div>

    <div class="overflow-x-auto rounded-lg border border-n-weak bg-n-solid-1">
      <table class="min-w-full text-sm">
        <thead class="bg-n-alpha-2 text-left text-n-slate-11">
          <tr>
            <th class="px-3 py-2">{{ t('TELEGRAM_INTERNAL.AUDIT.EVENT') }}</th>
            <th class="px-3 py-2">{{ t('TELEGRAM_INTERNAL.AUDIT.COUNT') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="item in eventSummaryRows"
            :key="item.name"
            class="border-t border-n-weak"
          >
            <td class="px-3 py-2 text-n-slate-12">{{ item.name }}</td>
            <td class="px-3 py-2 text-n-slate-12">{{ item.count }}</td>
          </tr>
          <tr v-if="!eventSummaryRows.length && !isLoading">
            <td class="px-3 py-6 text-n-slate-11" colspan="2">
              {{ t('TELEGRAM_INTERNAL.TABLE.EMPTY') }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="overflow-x-auto rounded-lg border border-n-weak bg-n-solid-1">
      <table class="min-w-full text-sm">
        <thead class="bg-n-alpha-2 text-left text-n-slate-11">
          <tr>
            <th class="px-3 py-2">{{ t('TELEGRAM_INTERNAL.AUDIT.TIME') }}</th>
            <th class="px-3 py-2">{{ t('TELEGRAM_INTERNAL.AUDIT.EVENT') }}</th>
            <th class="px-3 py-2">{{ t('TELEGRAM_INTERNAL.AUDIT.ACTOR') }}</th>
            <th class="px-3 py-2">{{ t('TELEGRAM_INTERNAL.AUDIT.INBOX') }}</th>
            <th class="px-3 py-2">
              {{ t('TELEGRAM_INTERNAL.AUDIT.CONVERSATION') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="item in rows"
            :key="item.id"
            class="border-t border-n-weak"
          >
            <td class="px-3 py-2 text-n-slate-12">
              {{ new Date(item.created_at).toLocaleString() }}
            </td>
            <td class="px-3 py-2 text-n-slate-12">{{ item.name }}</td>
            <td class="px-3 py-2 text-n-slate-12">
              {{ item.actor || `#${item.user_id || '-'}` }}
            </td>
            <td class="px-3 py-2 text-n-slate-12">
              {{ item.inbox_name || '-' }}
            </td>
            <td class="px-3 py-2 text-n-slate-12">
              {{ item.conversation_display_id || '-' }}
            </td>
          </tr>
          <tr v-if="!rows.length && !isLoading">
            <td class="px-3 py-6 text-n-slate-11" colspan="5">
              {{ t('TELEGRAM_INTERNAL.TABLE.EMPTY') }}
            </td>
          </tr>
          <tr v-if="isLoading">
            <td class="px-3 py-6 text-n-slate-11" colspan="5">
              {{ t('TELEGRAM_INTERNAL.LOADING') }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
